import React from 'react';
import { useTranslation } from 'react-i18next';
import { EvaluationLessonCard } from '@/components/LessonDetailsModal/EvaluationLessonCard';
import { LessonDetailsSection } from '@/components/LessonDetailsModal/LessonDetailsSection';
import { LessonScheduledTestCard } from '@/components/LessonDetailsModal/LessonScheduledTestCard';
import { LessonTopicsCard } from '@/components/LessonDetailsModal/LessonTopicsCard';
import type { Attachment } from '@/types/attachment';
import type { DiaryLesson } from '@/types/diary';
import type { EvaluationDetail } from '@/types/overview';
import { hasDraftTopic } from '@/utils/diary';

interface Props {
  lesson: DiaryLesson | null;
  evaluation: EvaluationDetail | undefined;
  onPreview: (attachment: Attachment) => void;
}

export const LessonTopic: React.FC<Props> = ({
  lesson,
  evaluation,
  onPreview,
}) => {
  const { t } = useTranslation();

  if (!lesson && evaluation) {
    return (
      <EvaluationLessonCard lesson={evaluation.lesson} onPreview={onPreview} />
    );
  }

  if (!lesson) {
    return null;
  }

  const { topics, scheduledTests } = lesson;
  const isTopicCardShown = topics.length > 0 || scheduledTests.length === 0;
  const title = hasDraftTopic(topics)
    ? t('diary.plannedLessonTopic', { count: topics.length })
    : t('diary.table.topic');

  return (
    <LessonDetailsSection title={title}>
      {scheduledTests.map((test, index) => (
        <LessonScheduledTestCard
          key={index}
          test={test}
          onPreview={onPreview}
        />
      ))}
      {isTopicCardShown && evaluation && (
        <EvaluationLessonCard
          lesson={evaluation.lesson}
          diaryTopics={lesson.topics}
          onPreview={onPreview}
        />
      )}
      {isTopicCardShown && !evaluation && (
        <LessonTopicsCard topics={lesson.topics} onPreview={onPreview} />
      )}
    </LessonDetailsSection>
  );
};
